/**
 * Whether the card for a dock row's term already holds that row's dictionary entry.
 *
 * Its own pure module because `npm test` has no DOM, so this is the only way to cover it — the
 * same reason `resultBadges`, `cardSignal` and `tokens` sit beside `Read.tsx` rather than inside
 * it.
 */

/** `card-entries`' answer: for each term, the JMdict `ent_seq`s its card already records. */
export type CardEntries = Readonly<Record<string, readonly number[]>>;

/**
 * Does the card fronted `term` already hold entry `seq`?
 *
 * **Ask per term, never across them.** This used to be one flat `Set` of every seq from every
 * term in the lookup, which happened to work while a row meant a dictionary entry: two rows could
 * only share a seq if the same entry matched twice, and a lookup rarely had two carded terms.
 *
 * 日本-290 makes sharing the normal case. A row is now an (entry × written form) pair, so the
 * 覇王樹, 仙人掌 and サボテン rows all carry seq 1387180 **by construction** — and under a flat set
 * carding any one of them would flip all three to `✓ on card`, offering to add nothing to two
 * cards that hold nothing.
 *
 * An unknown term answers `false`, which is the safe direction: the row offers to add, and the
 * server refuses a genuine duplicate anyway.
 */
export function isOnCard(
  entries: CardEntries,
  term: string | null | undefined,
  seq: number,
): boolean {
  if (!term) return false;
  return entries[term]?.includes(seq) ?? false;
}
